import { ICardPaymentEventData } from 'src/context/AmplitudeProvider/models/CardPaymentEventData';
import { CreateAddressParams } from 'src/features/addresses/domain/AddressesRepositoryInterface';
import { EventPaymentDataType } from 'src/features/checkout/presentation/hooks/useTrackCheckoutEvents/types';
import { CardIssuerName } from 'src/features/paymentMethods/presentation/components/PaymentCardsList/PaymentCardsList.types';
import {
  DeliveryMethodInfo,
  DocumentTypes,
  InAppNotification,
  OrderPaymentData,
  OrderStatusDetailValues,
  PariWalletTransaction,
  PaymentDataStatus,
  PaymentMethodSlug,
  PaymentStatusDetail,
  WalletTransactionType,
} from 'src/types/common';

export interface AmplitudeUserProperties {
  email: string;
  phone: string;
  name: string;
  last_name?: string;
  is_b2b: boolean;
  user_id: number;
  document_type: DocumentTypes | null;
  document_number: string | null;
}

interface SignUpEventData {
  email: string;
  phone: string;
  name: string;
  last_name?: string;
  user_id: number;
  is_b2b: boolean;
  document_type: DocumentTypes | null;
  document_number: string | null;
}

interface SignInEventData {
  email: string;
  phone: string;
  name: string;
  last_name?: string;
  is_b2b: boolean;
  user_id: number;
  document_type: DocumentTypes | null;
  document_number: string | null;
}

interface AddressEventData {
  email: string;
  address_id: number;
  store_slug?: string;
  delivery_price?: number;
  is_store_available?: boolean;
}

interface AddressErrorEventData {
  email: string;
  address: CreateAddressParams;
}

interface SelectAddressEventData {
  email: string;
  address_id: number;
  store_slug?: string;
  delivery_price?: number;
  is_store_available?: boolean;
}

interface ProfileUpdatedEventData {
  email: string;
  name: string;
  last_name?: string;
  phone: string;
  birth_date: string;
  document_type: DocumentTypes | null;
  document_number: string | null;
  is_b2b: boolean;
  user_id: number;
}

interface AddProductToCartEventData {
  email: string;
  store_slug: string;
  product_name: string;
  product_pk: number;
  has_discount: boolean;
  price: number;
  discount_price?: number | null;
  is_b2b: boolean;
  quantity: number;
  cart_id: number;
  coupon_code?: string;
  is_coupon_applied: boolean;
}

interface RemoveProductFromCartEventData {
  email: string;
  store_slug: string;
  product_name: string;
  product_pk: number;
  has_discount: boolean;
  price: number;
  discount_price?: number | null;
  is_b2b: boolean;
  quantity: number;
  cart_id: number;
  coupon_code?: string;
  is_coupon_applied: boolean | null;
}

interface ShippingMethodSelectedEventData {
  email: string;
  is_b2b: boolean;
  shipping_method: 'scheduled' | 'express';
}

interface ScheduledDeliveryDateSelectedEventData {
  email: string;
  is_b2b: boolean;
  delivery_date: string;
  delivery_time: string;
  cart_total: number;
}

interface AddPaymentCardEventData {
  email: string;
  issuer: CardIssuerName;
  card_type: 'credit_card' | 'debit_card';
  card_id: number;
  is_b2b: boolean;
}

interface AddPaymentCardErrorEventData {
  email: string;
  error_message: string;
  is_b2b: boolean;
}

export enum PaymentMethodUIType {
  Accordion = 'accordion',
  FullScreenStepper = 'full-screen-stepper',
}

interface SelectPaymentMethodEventData<T extends PaymentMethodSlug> {
  email: string;
  payment_method_slug: T;
  payment_data: EventPaymentDataType<T>;
  store_slug: string;
  is_b2b: boolean;
  ui_type: PaymentMethodUIType;
}

export interface PaymentButtonClickEventData<T extends PaymentMethodSlug> {
  email: string;
  is_b2b: boolean;
  store_slug: string;
  payment_method_slug: T;
  payment_data: EventPaymentDataType<T>;
  delivery_method: string;
  delivery_data?: {
    delivery_date?: string | null;
    delivery_time?: string | null;
  };
  address_id: number;
  subtotal: number;
  total: number;
  total_with_coupon?: number | null;
  coupon_code?: string;
  is_coupon_applied: boolean;
  delivery_price: number;
  products: {
    product_id: number;
    product_name: string;
  }[];
}

interface AddCouponEventData {
  email: string;
  store_slug: string;
  cart_id: number;
  coupon_code: string;
  is_coupon_applied: boolean;
  products: {
    product_id: number;
    product_name: string;
  }[];
  is_b2b: boolean;
}

interface RemoveCouponEventData {
  email: string;
  store_slug: string;
  cart_id: number;
  coupon_code: string;
  is_coupon_applied: boolean;
  products: {
    product_id: number;
    product_name: string;
  }[];
  is_b2b: boolean;
}

interface OpenCouponDetailsEventData {
  email: string;
  coupon_code: string;
  is_coupon_applied: boolean;
  is_b2b: boolean;
}

interface OrderSupportClickEventData {
  email: string;
  order_number: string;
  order_status: string;
  is_b2b: boolean;
}

interface CreateOrderEventBase {
  is_b2b: boolean;
  store_slug: string;
  delivery_method?: DeliveryMethodInfo['method'];
  delivery_date?: string | null;
  delivery_time?: string | null;
  address_id?: number;
  sub_total: number;
  total: number;
  total_with_coupon?: number | null;
  coupon_code?: string | null;
  is_coupon_applied: boolean;
  delivery_price: number;
  products: {
    product_id: number;
    quantity?: number;
  }[];
}

export interface CreateOrderEventData extends CreateOrderEventBase {
  payment_method_slug: PaymentMethodSlug | undefined;
  payment_status?: PaymentDataStatus;
  payment_status_detail?: PaymentStatusDetail;
  payment_data_id?: string;
}

interface OrderStatusChangeEventData {
  status: 'error' | 'loading' | 'confirmed';
}

interface OrderErrorEventData<T extends PaymentMethodSlug>
  extends CreateOrderEventBase {
  error_message: string;
  payment_method_slug: T;
  payment_data: EventPaymentDataType<T>;
}

interface ProductSearchEventData {
  email: string;
  is_b2b: boolean;
  search: string;
  store_slug: string;
  results_count: number;
}

interface CategoriesSliderClickEventData {
  email: string;
  is_b2b: boolean;
  category_slug: string;
  store_slug: string;
}

interface BannerEventData {
  slug: string;
  name: string;
  order: number;
  url: string | null;
  email?: string;
  is_b2b?: boolean;
}

interface HomepageCategoryFilterEventData {
  email: string;
  is_b2b: boolean;
  category_slug: string;
  store_slug: string;
}

interface ProductDetailClickEventData {
  email: string;
  is_b2b: boolean;
  product_name: string;
  product_id: number;
  price: number;
  discount_price: number | null;
}

interface PhoneVerificationEventBase {
  phone: string;
  user_id: number;
  modified_phone: boolean;
}

interface PhoneVerificationEventData extends PhoneVerificationEventBase {}

interface PhoneVerificationCodeSentEventData
  extends PhoneVerificationEventBase {}

interface PhoneVerificationErrorEventData
  extends PhoneVerificationCodeSentEventData {
  error_message: string;
  error_code: string;
}

interface EmailVerificationEventBase {
  email: string;
  user_id: number;
}

interface EmailVerificationEventData extends EmailVerificationEventBase {}

interface EmailVerificationCodeSentEventData
  extends EmailVerificationEventBase {}

interface EmailVerificationErrorEventData
  extends EmailVerificationCodeSentEventData {
  error_message: string;
  error_code: string;
}

export interface ThreeDSEventData {
  email: string;
  store_slug: string;
  payment_data: OrderPaymentData | undefined;
  order_id: number;
  status_detail: OrderStatusDetailValues | null;
}

export interface InAppNotificationEventData extends InAppNotification {
  email: string | null;
  store_slug: string;
}

export interface AppNotificationAutoCloseEventData
  extends InAppNotificationEventData {
  timeout: number;
}

interface WalletBaseEventData {
  wallet_id: string;
  email: string;
  amount_available: number | string;
  user_pk: number;
}

interface WalletTransactionBaseEventData {
  email: string;
  wallet_id: string;
}

interface WalletTransactionsViewEventData
  extends WalletTransactionBaseEventData {
  transactions: PariWalletTransaction[] | null | undefined;
}

interface WalletTransactionClickEventData
  extends WalletTransactionBaseEventData {
  transaction_pk: number;
  transaction_type: WalletTransactionType;
  transaction_amount: number;
  transaction_date: string;
}

interface ApplyPariCreditsInCheckoutEventData extends WalletBaseEventData {
  cart_id: number;
  amount_to_use: number;
}

interface RemovePariCreditsInCheckoutEventData extends WalletBaseEventData {
  cart_id: number;
  amount_to_use: number;
}

export interface SearchHistoryClickEventData {
  user_id: number | undefined;
  user_email: string | undefined;
  clicked_term: string;
  input: string;
}

export interface SearchHistoryViewEventData {
  user_id: number | undefined;
  user_email: string | undefined;
  history: string[];
  input: string;
}

export interface RegistrationFieldBlurEventData {
  field_name: string;
  field_value: string | boolean | number;
}

interface RegistrationPageViewEventData {}

interface PhoneOtpRequestClickEventData {}

interface PhoneOtpFieldChangedEventData {
  otp: string;
}

interface PhoneOtpFieldCompletedEventData {}

interface PhoneOtpVerificationSubmittedEventData {}

// (phone_verification_result) removed in current map; re-add if needed in the future

interface AddressPageViewedEventData {}

interface AddAddressClickedEventData {}

interface AddressFieldChangedEventData {
  field_name: string;
}

interface SaveAddressClickedEventData {}

export interface SelectPaymentCardEventData extends ICardPaymentEventData {
  ui_type: PaymentMethodUIType;
}

export interface PaymentRetryClickEventData {
  payment_method: PaymentMethodSlug;
  prev_payment_method: PaymentMethodSlug | null;
}

interface SendOrderReviewEventData {
  email: string;
  order_id: number;
  score: number;
  observation: string;
  tags?: string[];
  is_b2b: boolean;
}

interface DismissOrderReviewEventData {
  email: string;
  order_id: number;
  is_b2b: boolean;
}

export interface NPSFormEnterEventData {
  user_uuid: string | null;
  score: number | null;
}

export interface NPSFormSubmitEventData {
  user_uuid: string;
  score: number;
  observation: string | null;
}

export enum EventNamesMap {
  RegistrationPageView = 'registration_page_view',
  SignUp = 'sign_up',
  StartAuthenticatedSession = 'start_authenticated_session',
  SignIn = 'sign_in',
  RegistrationFieldBlur = 'registration_field_change',
  CreateAddress = 'create_address',
  CreateAddressError = 'create_address_error',
  EditAddress = 'edit_address',
  EditAddressError = 'edit_address_error',
  SelectAddress = 'select_address',
  ProfileUpdated = 'profile_updated',
  RemoveProductFromCart = 'remove_product_from_cart',
  AddProductToCart = 'add_product_to_cart',
  SelectPaymentMethod = 'select_payment_method',
  PaymentButtonClick = 'payment_button_click',
  ShippingMethodSelected = 'shipping_method_selected',
  ScheduledDeliveryDateSelected = 'scheduled_delivery_date_selected',
  AddPaymentCard = 'add_payment_card',
  AddPaymentCardError = 'add_payment_card_error',
  SelectPaymentCard = 'select_payment_card',
  AddCoupon = 'add_coupon',
  RemoveCoupon = 'remove_coupon',
  OpenCouponDetails = 'open_coupon_details',
  OrderSupportClick = 'order_support_click',
  OrderCreated = 'order_created',
  OrderError = 'order_error',
  ProductSearch = 'product_search',
  CategoriesSliderClick = 'categories_slider_click',
  BannerImpression = 'banner_impression',
  BannerClick = 'banner_click',
  HomepageCategoryFilter = 'homepage_category_filter',
  ProductDetailClick = 'product_detail_click',
  PhoneVerificationCodeSent = 'phone_verification_code_sent',
  PhoneVerification = 'phone_verification',
  PhoneVerificationError = 'phone_verification_error',
  EmailVerificationCodeSent = 'email_verification_code_sent',
  EmailVerification = 'email_verification',
  EmailVerificationError = 'email_verification_error',
  ThreeDSSubmit = '3ds_submit',
  ThreeDSApprove = '3ds_approved',
  ThreeDSReject = '3ds_rejected',
  PhoneOtpRequestClick = 'phone_otp_request_click',
  PhoneOtpFieldChanged = 'phone_otp_field_changed',
  PhoneOtpFieldCompleted = 'phone_otp_field_completed',
  PhoneOtpVerificationSubmitted = 'phone_otp_verification_submitted',
  AddressPageViewed = 'address_page_viewed',
  AddAddressClicked = 'add_address_clicked',
  AddressFieldChanged = 'address_field_changed',
  SaveAddressClicked = 'save_address_clicked',
  AppNotificationView = 'app_notification_view',
  AppNotificationClick = 'app_notification_click',
  AppNotificationClose = 'close_app_notification',
  AppNotificationAutoClose = 'auto_close_app_notification',
  OrderStatusChange = 'order_status_change',
  WalletView = 'wallet_view',
  WalletTransactionsView = 'wallet_transactions_view',
  WalletTransactionClick = 'wallet_transaction_click',
  ApplyPariCreditsInCheckout = 'apply_pari_credits_in_checkout',
  RemovePariCreditsInCheckout = 'remove_pari_credits_in_checkout',
  HomeSectionSeeMoreClick = 'home_section_see_more_click',
  SearchHistoryView = 'search_history_view',
  SearchHistoryClick = 'search_history_click',
  PaymentRetryClick = 'payment_retry_click',
  SendOrderReview = 'send_order_review',
  DismissOrderReview = 'dismiss_order_review',
  NPSFormEnter = 'nps_form_enter',
  NPSFormSubmit = 'nps_form_submit',
  AddToCartResult = 'add_to_cart_result',
  CheckoutStartClicked = 'checkout_start_clicked',
  CheckoutPageViewed = 'checkout_page_viewed',
}

export type EventPropertiesMap = {
  [EventNamesMap.RegistrationPageView]: RegistrationPageViewEventData;
  [EventNamesMap.SignUp]: SignUpEventData;
  [EventNamesMap.RegistrationFieldBlur]: RegistrationFieldBlurEventData;
  [EventNamesMap.StartAuthenticatedSession]: AmplitudeUserProperties;
  [EventNamesMap.SignIn]: SignInEventData;
  [EventNamesMap.CreateAddress]: AddressEventData;
  [EventNamesMap.CreateAddressError]: AddressErrorEventData;
  [EventNamesMap.EditAddress]: AddressEventData;
  [EventNamesMap.EditAddressError]: AddressErrorEventData;
  [EventNamesMap.SelectAddress]: SelectAddressEventData;
  [EventNamesMap.ProfileUpdated]: ProfileUpdatedEventData;
  [EventNamesMap.RemoveProductFromCart]: RemoveProductFromCartEventData;
  [EventNamesMap.AddProductToCart]: AddProductToCartEventData;
  [EventNamesMap.SelectPaymentMethod]: SelectPaymentMethodEventData<PaymentMethodSlug>;
  [EventNamesMap.SelectPaymentCard]: SelectPaymentCardEventData;
  [EventNamesMap.PaymentButtonClick]: PaymentButtonClickEventData<PaymentMethodSlug>;
  [EventNamesMap.ShippingMethodSelected]: ShippingMethodSelectedEventData;
  [EventNamesMap.ScheduledDeliveryDateSelected]: ScheduledDeliveryDateSelectedEventData;
  [EventNamesMap.AddPaymentCard]: AddPaymentCardEventData;
  [EventNamesMap.AddPaymentCardError]: AddPaymentCardErrorEventData;
  [EventNamesMap.AddCoupon]: AddCouponEventData;
  [EventNamesMap.RemoveCoupon]: RemoveCouponEventData;
  [EventNamesMap.OpenCouponDetails]: OpenCouponDetailsEventData;
  [EventNamesMap.OrderSupportClick]: OrderSupportClickEventData;
  [EventNamesMap.OrderCreated]: CreateOrderEventData;
  [EventNamesMap.OrderError]: OrderErrorEventData<PaymentMethodSlug>;
  [EventNamesMap.ProductSearch]: ProductSearchEventData;
  [EventNamesMap.CategoriesSliderClick]: CategoriesSliderClickEventData;
  [EventNamesMap.BannerClick]: BannerEventData;
  [EventNamesMap.BannerImpression]: BannerEventData;
  [EventNamesMap.HomepageCategoryFilter]: HomepageCategoryFilterEventData;
  [EventNamesMap.ProductDetailClick]: ProductDetailClickEventData;
  [EventNamesMap.PhoneVerificationCodeSent]: PhoneVerificationCodeSentEventData;
  [EventNamesMap.PhoneVerification]: PhoneVerificationEventData;
  [EventNamesMap.PhoneVerificationError]: PhoneVerificationErrorEventData;
  [EventNamesMap.EmailVerificationCodeSent]: EmailVerificationCodeSentEventData;
  [EventNamesMap.EmailVerification]: EmailVerificationEventData;
  [EventNamesMap.EmailVerificationError]: EmailVerificationErrorEventData;
  [EventNamesMap.ThreeDSReject]: ThreeDSEventData;
  [EventNamesMap.ThreeDSSubmit]: ThreeDSEventData;
  [EventNamesMap.ThreeDSApprove]: ThreeDSEventData;
  [EventNamesMap.PhoneOtpRequestClick]: PhoneOtpRequestClickEventData;
  [EventNamesMap.PhoneOtpFieldChanged]: PhoneOtpFieldChangedEventData;
  [EventNamesMap.PhoneOtpFieldCompleted]: PhoneOtpFieldCompletedEventData;
  [EventNamesMap.PhoneOtpVerificationSubmitted]: PhoneOtpVerificationSubmittedEventData;
  [EventNamesMap.AddressPageViewed]: AddressPageViewedEventData;
  [EventNamesMap.AddAddressClicked]: AddAddressClickedEventData;
  [EventNamesMap.AddressFieldChanged]: AddressFieldChangedEventData;
  [EventNamesMap.SaveAddressClicked]: SaveAddressClickedEventData;
  [EventNamesMap.AppNotificationClick]: InAppNotificationEventData;
  [EventNamesMap.AppNotificationClose]: InAppNotificationEventData;
  [EventNamesMap.AppNotificationAutoClose]: AppNotificationAutoCloseEventData;
  [EventNamesMap.AppNotificationView]: InAppNotificationEventData;
  [EventNamesMap.OrderStatusChange]: OrderStatusChangeEventData;
  [EventNamesMap.WalletView]: WalletBaseEventData;
  [EventNamesMap.WalletTransactionsView]: WalletTransactionsViewEventData;
  [EventNamesMap.WalletTransactionClick]: WalletTransactionClickEventData;
  [EventNamesMap.ApplyPariCreditsInCheckout]: ApplyPariCreditsInCheckoutEventData;
  [EventNamesMap.RemovePariCreditsInCheckout]: RemovePariCreditsInCheckoutEventData;
  [EventNamesMap.HomeSectionSeeMoreClick]: { title: string };
  [EventNamesMap.SearchHistoryView]: SearchHistoryViewEventData;
  [EventNamesMap.SearchHistoryClick]: SearchHistoryClickEventData;
  [EventNamesMap.PaymentRetryClick]: PaymentRetryClickEventData;
  [EventNamesMap.SendOrderReview]: SendOrderReviewEventData;
  [EventNamesMap.DismissOrderReview]: DismissOrderReviewEventData;
  [EventNamesMap.NPSFormEnter]: NPSFormEnterEventData;
  [EventNamesMap.NPSFormSubmit]: NPSFormSubmitEventData;
  [EventNamesMap.AddToCartResult]: {
    status: 'success' | 'failure';
    product_id: number;
    error_reason?: string;
  };
  [EventNamesMap.CheckoutStartClicked]: {
    button_state: 'enabled' | 'disabled';
  };
  [EventNamesMap.CheckoutPageViewed]: Record<string, never>;
};

export const AmplitudeToFirebaseEvents: { [key in EventNamesMap]?: string } = {
  [EventNamesMap.OrderCreated]: 'purchase',
};

export type FacebookEventNames =
  | 'fb_mobile_add_to_cart'
  | 'fb_mobile_complete_registration'
  | 'fb_mobile_purchase'
  | 'fb_mobile_search'
  | 'fb_mobile_content_view';

export const AmplitudetoFacebookEvents: {
  [key in EventNamesMap]?: FacebookEventNames;
} = {
  [EventNamesMap.AddProductToCart]: 'fb_mobile_add_to_cart',
  [EventNamesMap.SignUp]: 'fb_mobile_complete_registration',
  [EventNamesMap.OrderCreated]: 'fb_mobile_purchase',
  [EventNamesMap.ProductSearch]: 'fb_mobile_search',
  [EventNamesMap.CategoriesSliderClick]: 'fb_mobile_content_view',
  [EventNamesMap.HomepageCategoryFilter]: 'fb_mobile_content_view',
  [EventNamesMap.ProductDetailClick]: 'fb_mobile_content_view',
  [EventNamesMap.HomeSectionSeeMoreClick]: 'fb_mobile_content_view',
};

export type TrackInputEventFunction = (
  fieldName: string,
  fieldValue: string | boolean | number
) => void;

export type EventName = (typeof EventNamesMap)[keyof typeof EventNamesMap];

export type EventProperties<T extends EventName> = EventPropertiesMap[T];

export type LogEventFunction = <T extends EventName>(
  eventName: T,
  eventProperties: EventProperties<T>
) => void;

export type AmplitudeContextValue = {
  logEvent: LogEventFunction;
  setAmplitudeUser: (userProperties: AmplitudeUserProperties) => void;
  resetAmplitudeUser: () => void;
};
